import { HttpClient } from "@angular/common/http";
import { Injectable, computed, signal } from "@angular/core";
import { BehaviorSubject, catchError, map, Observable, tap, throwError } from "rxjs";
import { environment } from "../../../environments/environment";

export type AuthUser = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  registerName: string;
  permissions: string[];
};

type LoginResponse = {
  accessToken: string;
  user: AuthUser;
};

type RefreshResponse = {
  accessToken: string;
};

@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly tokenKey = "migestor_token";
  private readonly userKey = "migestor_user";
  private readonly userSignal = signal<AuthUser | null>(this.readStoredUser());

  readonly user = this.userSignal.asReadonly();
  readonly isAuthenticated = computed(() => Boolean(this.userSignal()));

  hasPermission(permission: string): boolean {
    const u = this.userSignal();
    if (!u) return false;
    if (u.role === "admin") return true;
    return u.permissions?.includes(permission) ?? false;
  }

  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(private readonly http: HttpClient) {}

  get token(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  login(email: string, password: string) {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, { email, password }, { withCredentials: true }).pipe(
      tap((response) => {
        localStorage.setItem(this.tokenKey, response.accessToken);
        localStorage.setItem(this.userKey, JSON.stringify(response.user));
        this.userSignal.set(response.user);
      })
    );
  }

  refreshToken(): Observable<string> {
    if (this.isRefreshing) {
      return new Observable<string>((observer) => {
        const subscription = this.refreshSubject.subscribe((token) => {
          if (token) {
            observer.next(token);
            observer.complete();
          }
        });
        return () => subscription.unsubscribe();
      });
    }

    this.isRefreshing = true;

    return this.http.post<RefreshResponse>(`${environment.apiUrl}/auth/refresh`, {}, { withCredentials: true }).pipe(
      map((response) => response.accessToken),
      tap((accessToken) => {
        this.isRefreshing = false;
        localStorage.setItem(this.tokenKey, accessToken);
        this.refreshSubject.next(accessToken);
      }),
      catchError((error) => {
        this.isRefreshing = false;
        this.refreshSubject.next(null);
        this.logout().subscribe();
        return throwError(() => error);
      })
    );
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/auth/logout`, {}, { withCredentials: true }).pipe(
      tap(() => this.clearSession()),
      catchError(() => {
        this.clearSession();
        return new Observable<void>((observer) => {
          observer.next();
          observer.complete();
        });
      })
    );
  }

  private clearSession(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    this.userSignal.set(null);
  }

  private readStoredUser(): AuthUser | null {
    const rawUser = localStorage.getItem(this.userKey);
    return rawUser ? (JSON.parse(rawUser) as AuthUser) : null;
  }
}
